import { api } from '../services/api';

function safeHttpUrl(value) {
  try {
    const parsed = new URL(String(value || ''));
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed : null;
  } catch {
    return null;
  }
}

function downloadName(parsed, explicitName) {
  if (typeof explicitName === 'string' && explicitName.trim()) return explicitName.trim();
  try {
    return decodeURIComponent(parsed.pathname.split('/').pop() || 'download');
  } catch {
    return parsed.pathname.split('/').pop() || 'download';
  }
}

/**
 * Honour the native session card's host verbs on the Playground surface.
 *
 * SessionBlockCard deliberately owns no navigation or file I/O. It emits a
 * `vi-card-action` event and every host must implement the verbs it supports.
 * The app surface already does this in App.jsx; Playground used to dispatch
 * the event without a listener, leaving Open and Save as inert buttons.
 */
export function handlePlaygroundCardAction(detail, {
  fallbackSessionId = null,
  sendAction = (...args) => api.sendSessionCardAction(...args),
} = {}) {
  const {
    action,
    cardId,
    sessionId,
    ...payload
  } = detail || {};
  delete payload.template;
  const sid = sessionId || fallbackSessionId;

  if (sid && cardId && action) {
    let sendResult;
    try {
      sendResult = sendAction(sid, cardId, action, payload);
    } catch (err) {
      console.error('[PlaygroundCardAction] Failed to send:', err);
    }
    Promise.resolve(sendResult).catch((err) => {
      console.error('[PlaygroundCardAction] Failed to send:', err);
    });
  }

  const parsed = safeHttpUrl(payload.url);
  if (!parsed) return false;

  if (action === 'open_url') {
    window.open(parsed.href, '_blank', 'noopener,noreferrer');
    return true;
  }

  if (action !== 'download_file') return false;

  // Cross-origin GCS URLs ignore an anchor's `download` attribute, so fetch
  // the blob first. If CORS/storage rejects that read, opening the signed URL
  // is an honest fallback instead of leaving the button inert.
  const filename = downloadName(parsed, payload.filename);
  fetch(parsed.href)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.blob();
    })
    .then((blob) => {
      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(blob);
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      setTimeout(() => {
        URL.revokeObjectURL(anchor.href);
        anchor.remove();
      }, 100);
    })
    .catch((err) => {
      console.error('[PlaygroundCardAction] Download failed, falling back to open:', err);
      window.open(parsed.href, '_blank', 'noopener,noreferrer');
    });
  return true;
}
